import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  List,
  Space,
  Spin,
  Tag,
  Typography
} from "antd";
import { ArrowLeftOutlined, EditOutlined, FilePdfOutlined, FormOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { getApprentice, getMeta, openWeek, periodPdfUrl } from "./api";
import { ApprenticeDialog, apprenticeStatusLabels } from "./Apprenticeship";
import { formatMinutes, mondayOf, weekLabel } from "./calc";
import { REPORT_STATUS_COLORS, reportStatusLabels } from "./labels";
import { formatDay } from "../safety/format";
import "../safety/safety.css";

/** One apprentice: record facts, missing weeks, the weeks written so far, period export. */
const ApprenticeWeeks = ({ apprenticeId, embedded = false }) => {
  const { t } = useTranslation("dashboard");
  const params = useParams();
  const id = apprenticeId || params.id;
  const history = useHistory();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [range, setRange] = useState(null);
  const [opening, setOpening] = useState("");
  const [editing, setEditing] = useState(false);
  const statuses = reportStatusLabels(t);
  const apprenticeStatuses = apprenticeStatusLabels(t);

  const load = () =>
    Promise.all([getMeta(), getApprentice(id)])
      .then(([metaRes, res]) => {
        setMeta(metaRes.data);
        setData(res.data);
      })
      .catch(generalErrorAlert);

  useEffect(() => {
    load();
  }, [id]);

  if (!data) {
    return (
      <div className="apprenticeship-page sf-center">
        <Spin />
      </div>
    );
  }

  const record = data.data;
  const self = record.relation === "self";
  const thisMonday = mondayOf(dayjs().format("YYYY-MM-DD"));

  const write = async weekStart => {
    setOpening(weekStart);
    try {
      const res = await openWeek(record._id, weekStart);
      history.push(`/user/berichtsheft/reports/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setOpening("");
    }
  };

  const currentWritten = data.reports.some(r => r.weekStart === thisMonday);
  const missing = data.missing || [];

  return (
    <div className="apprenticeship-page">
      {!embedded && (
        <div className="sf-header mb-3">
          <Button
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.goBack()}
          />
          <div className="sf-header-title">
            <Typography.Title level={4}>{record.name}</Typography.Title>
            {record.status !== "active" && <Tag>{apprenticeStatuses[record.status]}</Tag>}
          </div>
          {meta?.can.update && !self && (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
              {t("Edit")}
            </Button>
          )}
        </div>
      )}

      <Card size="small" className="mb-3">
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2, lg: 3 }}
          items={[
            { key: "trade", label: t("Trade"), children: record.trade },
            {
              key: "year",
              label: t("Training year"),
              children: record.trainingYearNow || "—"
            },
            {
              key: "period",
              label: t("Period"),
              children: `${formatDay(record.startDate)} – ${formatDay(record.endDate)}`
            },
            { key: "trainer", label: t("Trainer"), children: record.trainerName },
            { key: "school", label: t("Vocational school"), children: record.school || "—" },
            {
              key: "chamber",
              label: t("Chamber (HWK)"),
              children:
                [record.chamber, record.chamberRegistrationNumber].filter(Boolean).join(" · ") ||
                "—"
            },
            {
              key: "exams",
              label: t("Exams"),
              children:
                [
                  record.midExamDate &&
                    `${t("Intermediate exam")} ${formatDay(record.midExamDate)}`,
                  record.finalExamDate && `${t("Final exam")} ${formatDay(record.finalExamDate)}`
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"
            }
          ]}
        />
      </Card>

      {self && record.status === "active" && !currentWritten && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t("Week {{week}} is running", { week: weekLabel(thisMonday) })}
          action={
            <Button
              size="small"
              type="primary"
              loading={opening === thisMonday}
              onClick={() => write(thisMonday)}
            >
              {t("Write")}
            </Button>
          }
        />
      )}

      {missing.length > 0 && (
        <Card
          size="small"
          className="mb-3"
          title={
            <Typography.Text type="danger">
              {t("{{n}} weeks missing", { n: missing.length })}
            </Typography.Text>
          }
        >
          <List
            dataSource={missing}
            renderItem={week => {
              const existing = data.reports.find(r => r.weekStart === week.weekStart);
              return (
                <List.Item
                  actions={[
                    existing ? (
                      <Link key="o" to={`/user/berichtsheft/reports/${existing._id}`}>
                        {t("Open")}
                      </Link>
                    ) : self ? (
                      <Button
                        key="w"
                        size="small"
                        icon={<FormOutlined />}
                        loading={opening === week.weekStart}
                        onClick={() => write(week.weekStart)}
                      >
                        {t("Write")}
                      </Button>
                    ) : null
                  ]}
                >
                  <Space wrap>
                    <span>
                      {week.label} · {formatDay(week.weekStart)}
                    </span>
                    <Tag color={REPORT_STATUS_COLORS[week.state]}>{statuses[week.state]}</Tag>
                  </Space>
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      <Card
        size="small"
        title={t("Weeks")}
        extra={
          <Space wrap>
            <DatePicker.RangePicker
              size="small"
              format="DD.MM.YYYY"
              value={range}
              onChange={setRange}
              allowEmpty={[true, true]}
            />
            <Button
              size="small"
              icon={<FilePdfOutlined />}
              disabled={!data.reports.length}
              href={periodPdfUrl(
                record._id,
                range?.[0] ? range[0].format("YYYY-MM-DD") : "",
                range?.[1] ? range[1].format("YYYY-MM-DD") : ""
              )}
              target="_blank"
            >
              {t("Export PDF")}
            </Button>
          </Space>
        }
      >
        <ResponsiveTable
          rowKey="_id"
          dataSource={data.reports}
          mobileAvatar={false}
          locale={{ emptyText: t("No weeks written yet") }}
          pagination={data.reports.length > 30 ? { pageSize: 30 } : false}
          onRow={row => ({
            onDoubleClick: () => history.push(`/user/berichtsheft/reports/${row._id}`)
          })}
          columns={[
            {
              title: t("Week"),
              key: "week",
              mobile: "title",
              render: (_, row) => (
                <Link to={`/user/berichtsheft/reports/${row._id}`}>
                  {row.label} · {formatDay(row.weekStart)}
                </Link>
              )
            },
            {
              title: t("Training year"),
              dataIndex: "trainingYear",
              key: "year",
              mobile: "subtitle",
              render: value => t("Year {{n}}", { n: value })
            },
            {
              title: t("Status"),
              key: "status",
              mobile: "badge",
              render: (_, row) => (
                <Tag color={REPORT_STATUS_COLORS[row.status]}>{statuses[row.status]}</Tag>
              )
            },
            {
              title: t("Hours"),
              key: "hours",
              align: "right",
              render: (_, row) => formatMinutes(row.totalMinutes)
            }
          ]}
        />
      </Card>

      {meta?.can.update && (
        <ApprenticeDialog
          open={editing}
          record={record}
          meta={meta}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default ApprenticeWeeks;
